import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import usePagination from "../../../../hooks/usePagination";
import { FilterAutocomplete, Table } from "../../../../components/ui";
import { MASTER_BAN_LIST_COLUMNS } from "./constants";
import { complianceSearchActions } from "../../../../reducers/complianceSearch";
import { useComplianceSearchContext } from "../../../../context";
import { COMPLIANCE_API_URLS, ComplianceAPI } from "../../../../api/compliance";
import { appendSearchParamsToQuery } from "../../utils";
import { WBP_TAB_TITLES } from "../WatchListBanPnlTab/constants";
import newSortBy from "../../../../utils/newSortBy";
import { ISortBy } from "../../../../types";
import { SectionTitle } from "../../styledComponents";

const MasterBanList: React.FC = () => {
  const dispatch = useDispatch();
  const pagination = usePagination();

  const { tabsTablesSearchState, setTabsTablesSearchState } = useComplianceSearchContext();
  const { search } = tabsTablesSearchState.watchListBanPnlTab.masterBanTable;

  const [sortBy, setSortBy] = useState<ISortBy>({ by: "timestamp", order: false });

  const {
    loading,
    data: { results },
  } = useSelector((state) => state.complianceSearch.masterBanList);

  const { isError, error } = useQuery({
    retry: false,
    queryKey: [
      COMPLIANCE_API_URLS.getMasterBanList,
      Object.values(search),
      pagination.page,
      pagination.limit,
      sortBy,
    ],
    async queryFn({ signal }) {
      const query = new URLSearchParams();
      query.append("limit", pagination.limit.toString());
      query.append("page", pagination.page.toString());
      query.append("sort_by", sortBy.by);
      query.append("order", sortBy.order ? "ASC" : "DESC");

      appendSearchParamsToQuery(search, query);

      dispatch(complianceSearchActions.fetchMasterBanListRequest());
      const response = await ComplianceAPI.getMasterBanListRequest(query.toString(), signal);
      dispatch(complianceSearchActions.fetchMasterBanListSuccess(response.data));
      pagination.setCount(response.data.results.count);

      return response;
    },
  });

  useEffect(() => {
    if (isError && error) {
      console.error("Error fetching master ban list search", error);
      dispatch(complianceSearchActions.fetchMasterBanListFailed());
    }
  }, [isError, error]);

  const handleSortBy = (by: string) => newSortBy({ by, sortBy, setSortBy });

  useEffect(() => {
    pagination.setPage(0);
  }, [search, pagination.limit]);

  return (
    <SectionTitle title="Master ban list">
      <div>
        <FilterAutocomplete
          search={search}
          setSearch={(values) => {
            setTabsTablesSearchState({
              ...tabsTablesSearchState,
              watchListBanPnlTab: {
                ...tabsTablesSearchState.watchListBanPnlTab,
                masterBanTable: {
                  search: values,
                },
              },
            });
          }}
          filters={{
            Name: "names",
            Email: "email",
            "Entries ID": "compliance_entries_id",
          }}
        />
      </div>

      <Table
        columns={MASTER_BAN_LIST_COLUMNS}
        loading={loading}
        pagination={pagination}
        extractKey={(row) => row.id}
        data={results.rows}
        csv_table_name={WBP_TAB_TITLES.masterBanTable}
        handleSortBy={handleSortBy}
        sortBy={sortBy}
      />
    </SectionTitle>
  );
};

export default MasterBanList;
